// A8-i (I.160): Estoque › Balanço — painel do balanço de um depósito (pacote docs/pacotes/A8-i.md).
// Menu visível a quem tem conferência e ao master; abrir, mudar a data, cancelar e decretar só o master
// (decisão do Diretor de 15/10). O banco é quem decide: a tela só esconde os botões.
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  STOCK_BALANCE_PAGE_SIZE,
  useStockBalanceActions,
  useStockBalanceProgress,
  type StockBalanceList,
} from "@/hooks/useStockBalance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Scale, ChevronLeft, ChevronRight, Link2 } from "lucide-react";

// Hoje no fuso de São Paulo (AAAA-MM-DD), sem passar por UTC
const todaySaoPaulo = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date());

const fmtDate = (iso: string) => iso.split("-").reverse().join("/");
const fmtQty = (n: number) => Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 3 });

export const StockBalanceView = () => {
  const { company, userRole } = useAuth();
  // o tipo do front ainda não tem master_2 (fila E-MASTER2-TELA); o banco é quem decide
  const isMaster = (["master", "master_2"] as string[]).includes(userRole ?? "");

  const [list, setList] = useState<StockBalanceList>("not_counted");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useStockBalanceProgress(list, page);
  const { open, setValidSince, cancel, decree } = useStockBalanceActions();

  const [deposits, setDeposits] = useState<Array<{ id: string; name: string; is_main: boolean }>>([]);
  const [name, setName] = useState("");
  const [depositId, setDepositId] = useState<string>("");
  const [validSince, setValidSinceInput] = useState<string>(todaySaoPaulo());
  const [newDate, setNewDate] = useState<string>("");
  const [confirm, setConfirm] = useState<"cancel" | "decree" | null>(null);

  useEffect(() => {
    if (!company?.id || !isMaster) return;
    supabase.from("deposits").select("id, name, is_main").eq("company_id", company.id).eq("active", true).order("name")
      .then(({ data: deps }) => {
        const list = (deps ?? []) as Array<{ id: string; name: string; is_main: boolean }>;
        setDeposits(list);
        setDepositId((prev) => prev || list.find((d) => d.is_main)?.id || "");
      });
  }, [company?.id, isMaster]);

  const balance = data?.balance ?? null;
  useEffect(() => { if (balance) setNewDate(balance.counts_valid_since); }, [balance?.counts_valid_since]);

  const stats = data?.stats;
  const total = list === "no_cost" ? stats?.no_cost ?? 0 : stats?.not_counted ?? 0;
  const pages = Math.max(1, Math.ceil(total / STOCK_BALANCE_PAGE_SIZE));

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="space-y-4 max-w-xl">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Scale className="h-6 w-6" /> Balanço</h2>
        <Card>
          <CardHeader>
            <CardTitle>Nenhum balanço aberto</CardTitle>
            <CardDescription>
              Com o balanço aberto, as conferências do depósito dele viram partes do balanço; cada item contado acerta
              primeiro o custo médio e depois a quantidade.
            </CardDescription>
          </CardHeader>
          {isMaster && (
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="balance-name">Nome</Label>
                <Input id="balance-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Balanço dezembro 2026" />
              </div>
              <div className="space-y-1.5">
                <Label>Depósito</Label>
                <Select value={depositId} onValueChange={setDepositId}>
                  <SelectTrigger><SelectValue placeholder="Escolha o depósito" /></SelectTrigger>
                  <SelectContent>
                    {deposits.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}{d.is_main ? " (principal)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="balance-since">Contagens valem desde</Label>
                <Input id="balance-since" type="date" value={validSince} onChange={(e) => setValidSinceInput(e.target.value)} />
              </div>
              <Button
                onClick={() => open.mutate({ name, depositId, validSince })}
                disabled={open.isPending}
              >
                {open.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Abrir balanço
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Scale className="h-6 w-6" /> {balance.name}</h2>
          <p className="text-sm text-muted-foreground">
            Depósito {balance.deposit_name ?? "—"} · contagens valem desde {fmtDate(balance.counts_valid_since)}
          </p>
        </div>
        {isMaster && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="balance-new-date" className="text-xs">Mudar data</Label>
              <Input id="balance-new-date" type="date" className="h-9 w-40" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={setValidSince.isPending || !newDate || newDate === balance.counts_valid_since}
              onClick={() => setValidSince.mutate({ balanceId: balance.id, validSince: newDate })}
            >
              Salvar data
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirm("cancel")}>Cancelar balanço</Button>
            <Button size="sm" onClick={() => setConfirm("decree")}>Decretar</Button>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">No escopo</p><p className="text-2xl font-bold">{stats.in_scope}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Contados</p><p className="text-2xl font-bold text-green-600">{stats.counted}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Não conferidos</p><p className="text-2xl font-bold">{stats.not_counted}</p><p className="text-xs text-red-600">{stats.not_counted_negative} negativos</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Sem custo</p><p className="text-2xl font-bold text-red-600">{stats.no_cost}</p></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button variant={list === "not_counted" ? "default" : "outline"} size="sm" onClick={() => { setList("not_counted"); setPage(1); }}>
                Não conferidos
              </Button>
              <Button variant={list === "no_cost" ? "default" : "outline"} size="sm" onClick={() => { setList("no_cost"); setPage(1); }}>
                Sem custo
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span>{page} / {pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">SKU</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead className="w-[120px] text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows ?? []).map((row) => (
                <TableRow key={row.product_id}>
                  <TableCell className="font-mono text-sm text-muted-foreground">{row.sku ?? "—"}</TableCell>
                  <TableCell className="text-sm">{row.name}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {fmtQty(row.quantity)}
                    {row.negative && <Badge variant="outline" className="ml-2 text-[10px] bg-red-500/10 text-red-700 border-red-500/40">negativo</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {(data?.rows ?? []).length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">Nada nesta lista</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Partes do balanço</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data?.parts ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma parte ainda. Crie em Estoque › Conferência.</p>}
          {(data?.parts ?? []).map((part) => (
            <div key={part.id} className="flex items-center justify-between gap-2 text-sm">
              <span>{part.name}</span>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {part.status === "completed" ? "Finalizada" : part.status === "cancelled" ? "Cancelada" : "Aberta"}
                </Badge>
                {(part.status === "draft" || part.status === "in_progress") && (
                  <Button variant="ghost" size="sm" onClick={() => window.open(`${window.location.origin}/conferencia/${part.access_token}`, "_blank")}>
                    <Link2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "decree" ? "Decretar o balanço" : "Cancelar o balanço"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "decree"
                ? "O decreto fecha o balanço e grava o resultado. Não relança nada e não liga a trava de estoque. Partes abertas impedem o decreto."
                : "As partes abertas viram conferências comuns; as finalizadas ficam registradas. O estoque não muda."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm === "decree") decree.mutate(balance.id);
                if (confirm === "cancel") cancel.mutate(balance.id);
                setConfirm(null);
              }}
            >
              {confirm === "decree" ? "Decretar" : "Cancelar balanço"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
